import { round } from 'lodash'
import { HourSeriesResult } from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  DomesticHotWaterParameters,
  EnergySystemFragment,
  EnergySystemType,
  HourSeriesType,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { hoursInYear } from '../util/hours-in-year'
import { i18n } from '../i18n'

export const calcDhwCirculationLossDensity = (
  model: BuildingEnergyModelFragment,
  area: number,
): number => {
  const dhw = model.energySystems.find(
    (s) => s.type === EnergySystemType.DomesticHotWater,
  )

  if (!dhw) return 0

  const params = dhw.parameters as DomesticHotWaterParameters

  let heatLoss = 0
  let heaters = 0
  if (
    params.circulationSpecificLength &&
    params.circulationSpecificLength > 0 &&
    params.circulationSpecificHeatLoss &&
    params.circulationSpecificHeatLoss > 0
  ) {
    heatLoss =
      params.circulationSpecificLength * params.circulationSpecificHeatLoss
  }

  if (
    params.circulationHeatersAmount &&
    params.circulationHeatersAmount > 0 &&
    params.circulationHeatersPower &&
    params.circulationHeatersPower > 0 &&
    area > 0
  ) {
    heaters =
      (params.circulationHeatersAmount * params.circulationHeatersPower) / area
  }

  return heatLoss + heaters
}

export const calcDhwCirculationLossHourSeries = (
  model: BuildingEnergyModelFragment,
  area: number,
): number[] => {
  const dhw = model.energySystems.find(
    (s) => s.type === EnergySystemType.DomesticHotWater,
  )

  let serie = Array<number>(hoursInYear).fill(0)

  if (!dhw) return serie

  if (area === 0) return serie

  const params = dhw.parameters as DomesticHotWaterParameters

  let heatLoss = 0
  let heaters = 0
  if (
    params.circulationSpecificLength &&
    params.circulationSpecificLength > 0 &&
    params.circulationSpecificHeatLoss &&
    params.circulationSpecificHeatLoss > 0
  ) {
    heatLoss =
      params.circulationSpecificLength *
      params.circulationSpecificHeatLoss *
      area
  }

  if (
    params.circulationHeatersAmount &&
    params.circulationHeatersAmount > 0 &&
    params.circulationHeatersPower &&
    params.circulationHeatersPower > 0
  ) {
    heaters = params.circulationHeatersAmount * params.circulationHeatersPower
  }

  if (heatLoss + heaters > 0) {
    serie = Array<number>(hoursInYear).fill(
      round((heatLoss + heaters) / 1000, 2) * 1000,
    )
  }

  return serie
}

export const calcDhwConsumptionDensityHourSeries = (
  model: BuildingEnergyModelFragment,
  year: number,
): number[] => {
  const dhw = model.energySystems.find(
    (s) => s.type === EnergySystemType.DomesticHotWater,
  )

  let generalSerie = Array<number>(hoursInYear).fill(0)

  if (!dhw) return generalSerie

  const params = dhw.parameters as DomesticHotWaterParameters

  if (!dhw.schedule) return generalSerie

  // Initialize with the schedule factors serie
  generalSerie = calculateHourSerieFactors(dhw.schedule, year)
  const factorsSum = generalSerie.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0,
  )

  if (factorsSum === 0) return generalSerie

  for (let i = 0; i < generalSerie.length; i++) {
    generalSerie[i] *=
      ((params.consumptionDensity / params.distributionEfficiency) *
        params.consumptionFactor) /
      factorsSum
  }

  return generalSerie
}

export const dhwCalculator = (
  energySystem: EnergySystemFragment,
  energyModelArea: number,
  year: number,
): HourSeriesResult[] => {
  const params = energySystem.parameters as DomesticHotWaterParameters

  // General energy consumption
  let generalSerie: number[] = []
  if (energySystem.schedule) {
    // Initialize with the schedule factors serie
    generalSerie = calculateHourSerieFactors(energySystem.schedule, year)
    const factorsSum = generalSerie.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    )

    if (factorsSum > 0) {
      for (let i = 0; i < generalSerie.length; i++) {
        generalSerie[i] =
          round(
            (generalSerie[i] *
              ((((params.consumptionDensity / params.distributionEfficiency) *
                params.consumptionFactor) /
                factorsSum) *
                energyModelArea)) /
              1000,
            2,
          ) * 1000
      }
    }
  }

  // Circulation energy consumption
  let circulationSerie: number[] = []
  let heatLoss = 0
  let heaters = 0
  if (
    params.circulationSpecificLength &&
    params.circulationSpecificLength > 0 &&
    params.circulationSpecificHeatLoss &&
    params.circulationSpecificHeatLoss > 0
  ) {
    heatLoss =
      params.circulationSpecificLength *
      params.circulationSpecificHeatLoss *
      energyModelArea
  }

  if (
    params.circulationHeatersAmount &&
    params.circulationHeatersAmount > 0 &&
    params.circulationHeatersPower &&
    params.circulationHeatersPower > 0
  ) {
    heaters = params.circulationHeatersAmount * params.circulationHeatersPower
  }

  if (heatLoss + heaters > 0) {
    circulationSerie = Array.from(
      Array(hoursInYear).fill(
        round((heatLoss + heaters) / 1000, 2) * 1000,
      ) as number[],
    )
  }

  // Waste water energy saving
  let wasteWaterSerie: number[] = []
  if (
    params.wasteWaterHeatContentRatio &&
    params.wasteWaterHeatContentRatio > 0 &&
    generalSerie.length > 0
  ) {
    wasteWaterSerie = generalSerie.map(
      (v) => round((v * params.wasteWaterHeatContentRatio) / 1000, 2) * 1000,
    )
  }

  const resultSeries: HourSeriesResult[] = []
  if (generalSerie.length > 0)
    resultSeries.push({
      hourSerieType: HourSeriesType.HeatingDemand,
      energySystemType: EnergySystemType.DomesticHotWater,
      consumptionEnergyType: ConsumptionEnergyType.DhwHeating,
      hourSerieName: i18n.t('buildingEnergyModels.hourSeriesNames.dhwHeating'),
      hourSerie: generalSerie,
    })

  if (circulationSerie.length > 0)
    resultSeries.push({
      hourSerieType: HourSeriesType.HeatingDemand,
      energySystemType: EnergySystemType.DomesticHotWater,
      consumptionEnergyType: ConsumptionEnergyType.DhwCirculationHeating,
      hourSerieName: i18n.t(
        'buildingEnergyModels.hourSeriesNames.dhwCirculation',
      ),
      hourSerie: circulationSerie,
    })

  if (wasteWaterSerie.length > 0)
    resultSeries.push({
      hourSerieType: HourSeriesType.WasteHeating,
      energySystemType: EnergySystemType.DomesticHotWater,
      consumptionEnergyType: ConsumptionEnergyType.WasteWaterHeatRecovery,
      hourSerieName: i18n.t(
        'buildingEnergyModels.hourSeriesNames.dhwHeatRecovery',
      ),
      hourSerie: wasteWaterSerie,
    })

  return resultSeries
}
